import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, UploadCloud, X } from "lucide-react";
import api, { fileUrl, formatApiError } from "./api";

const INPUT = "bg-transparent border border-[#333333] focus:border-[#D4AF37] text-white text-sm p-2.5 w-full outline-none transition-colors";
const emptyForm = { title: "", location: "", description: "", services: "" };

export const PortfolioPanel = () => {
  const [projects, setProjects] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [photo, setPhoto] = useState(null);
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const fileRef = useRef(null);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!photo) return toast.error("Please choose a project photo.");
    if ((beforePhoto && !afterPhoto) || (!beforePhoto && afterPhoto))
      return toast.error("Add both a before and an after photo for the comparison slider.");
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("location", form.location);
      fd.append("description", form.description);
      form.services.split(",").map((s) => s.trim()).filter(Boolean).forEach((s) => fd.append("services", s));
      fd.append("photo", photo);
      if (beforePhoto) fd.append("before_photo", beforePhoto);
      if (afterPhoto) fd.append("after_photo", afterPhoto);
      await api.post("/admin/projects", fd);
      toast.success("Project added to the gallery");
      setForm(emptyForm);
      setPhoto(null);
      setBeforePhoto(null);
      setAfterPhoto(null);
      if (fileRef.current) fileRef.current.value = "";
      if (beforeRef.current) beforeRef.current.value = "";
      if (afterRef.current) afterRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const saveEdit = async (id) => {
    try {
      await api.patch(`/admin/projects/${id}`, {
        title: editForm.title,
        location: editForm.location,
        description: editForm.description,
        services: editForm.services.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Project updated");
      setEditId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this project from the public gallery?")) return;
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Project removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (projects === null)
    return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37] mx-auto" /></div>;

  return (
    <div className="grid lg:grid-cols-3 gap-8" data-testid="portfolio-panel">
      <form onSubmit={add} className="bg-[#111111] border border-[#D4AF37]/40 p-6 space-y-5 h-fit" data-testid="portfolio-add-form">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add New Work
        </p>
        <input required placeholder="Project title" className={INPUT} data-testid="portfolio-title-input"
          value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <input placeholder="Location (e.g. Chesterfield, VA)" className={INPUT} data-testid="portfolio-location-input"
          value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        <textarea required rows={4} placeholder="Description of the work performed" className={`${INPUT} resize-none`}
          data-testid="portfolio-description-input"
          value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <input placeholder="Services, comma separated" className={INPUT} data-testid="portfolio-services-input"
          value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} />
        <label className="flex items-center justify-center gap-2 border border-dashed border-[#333333] hover:border-[#D4AF37] transition-colors px-4 py-6 cursor-pointer text-gray-400 text-xs"
          data-testid="portfolio-photo-dropzone">
          <UploadCloud className="h-4 w-4 text-[#D4AF37]" />
          {photo ? photo.name : "Upload project photo"}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="portfolio-photo-input"
            onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col items-center justify-center gap-1.5 border border-dashed border-[#333333] hover:border-[#D4AF37] transition-colors px-3 py-4 cursor-pointer text-gray-400 text-[11px] text-center"
            data-testid="portfolio-before-dropzone">
            <UploadCloud className="h-4 w-4 text-[#D4AF37]" />
            {beforePhoto ? beforePhoto.name : "Before photo (optional)"}
            <input ref={beforeRef} type="file" accept="image/*" className="hidden" data-testid="portfolio-before-input"
              onChange={(e) => setBeforePhoto(e.target.files?.[0] || null)} />
          </label>
          <label className="flex flex-col items-center justify-center gap-1.5 border border-dashed border-[#333333] hover:border-[#D4AF37] transition-colors px-3 py-4 cursor-pointer text-gray-400 text-[11px] text-center"
            data-testid="portfolio-after-dropzone">
            <UploadCloud className="h-4 w-4 text-[#D4AF37]" />
            {afterPhoto ? afterPhoto.name : "After photo (optional)"}
            <input ref={afterRef} type="file" accept="image/*" className="hidden" data-testid="portfolio-after-input"
              onChange={(e) => setAfterPhoto(e.target.files?.[0] || null)} />
          </label>
        </div>
        <p className="text-[10px] text-gray-600">Add both before & after photos to give visitors a drag-to-compare slider.</p>
        <button type="submit" disabled={adding} data-testid="portfolio-add-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#050505] hover:bg-[#C9A227] transition-colors px-6 py-3 uppercase tracking-widest text-xs font-semibold disabled:opacity-60">
          {adding && <Loader2 className="h-4 w-4 animate-spin" />}
          {adding ? "Publishing..." : "Publish to Gallery"}
        </button>
      </form>

      <div className="lg:col-span-2 space-y-4">
        {projects.map((p) => (
          <div key={p.id} data-testid={`portfolio-item-${p.id}`}
            className="bg-[#111111] border border-[#333333] p-4 flex gap-5">
            <img src={fileUrl(p.img)} alt={p.title} className="w-28 h-28 object-cover border border-[#333333] shrink-0" />
            {editId === p.id ? (
              <div className="flex-1 space-y-3">
                <input className={INPUT} value={editForm.title} data-testid={`portfolio-edit-title-${p.id}`}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
                <input className={INPUT} value={editForm.location} placeholder="Location"
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} />
                <textarea rows={3} className={`${INPUT} resize-none`} value={editForm.description}
                  data-testid={`portfolio-edit-description-${p.id}`}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                <input className={INPUT} value={editForm.services} placeholder="Services, comma separated"
                  onChange={(e) => setEditForm({ ...editForm, services: e.target.value })} />
                <div className="flex gap-3">
                  <button onClick={() => saveEdit(p.id)} data-testid={`portfolio-edit-save-${p.id}`}
                    className="text-[11px] uppercase tracking-widest border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505] transition-colors px-4 py-2">
                    Save
                  </button>
                  <button onClick={() => setEditId(null)} data-testid={`portfolio-edit-cancel-${p.id}`}
                    className="text-[11px] uppercase tracking-widest text-gray-400 hover:text-white px-4 py-2">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-white font-serif text-lg">{p.title}</p>
                    <p className="text-[11px] text-[#D4AF37] uppercase tracking-widest">{p.location}</p>
                    {p.before_img && p.after_img && (
                      <span className="inline-block mt-1 text-[9px] uppercase tracking-widest border border-[#D4AF37]/50 text-[#D4AF37] px-2 py-0.5">
                        Before/After Slider
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditId(p.id); setEditForm({ title: p.title, location: p.location, description: p.description, services: (p.services || []).join(", ") }); }}
                      data-testid={`portfolio-edit-btn-${p.id}`} className="text-gray-400 hover:text-[#D4AF37] p-2 transition-colors" aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(p.id)} data-testid={`portfolio-delete-btn-${p.id}`}
                      className="text-gray-400 hover:text-red-400 p-2 transition-colors" aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2 leading-relaxed line-clamp-3">{p.description}</p>
                <p className="text-[11px] text-gray-500 mt-2">{(p.services || []).join(" • ")}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
